import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../lib/utils';

/* ---------- Types ---------- */

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration: number;
}

interface ToastContextValue {
  toast: (opts: {
    message: string;
    type?: Toast['type'];
    duration?: number;
  }) => void;
  toasts: Toast[];
}

/* ---------- Context ---------- */

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

/* ---------- Style maps ---------- */

const MAX_TOASTS = 5;

const typeStyles: Record<Toast['type'], string> = {
  success: 'bg-green-50 text-green-600',
  error: 'bg-red-50 text-red-600',
  warning: 'bg-amber-50 text-amber-600',
  info: 'bg-blue-50 text-blue-600',
};

const typeIcons: Record<Toast['type'], React.ReactNode> = {
  success: <CheckCircle2 size={18} />,
  error: <XCircle size={18} />,
  warning: <AlertTriangle size={18} />,
  info: <Info size={18} />,
};

/* ---------- Provider ---------- */

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (opts: { message: string; type?: Toast['type']; duration?: number }) => {
      const id = crypto.randomUUID();
      const type = opts.type ?? 'info';
      // Error toasts persist by default (require user dismiss via X) --
      // per Albert's 2026-05-19 feedback: actionable failures auto-
      // dismissing in 4s are missed by users still reading DevTools or
      // copying the message. Success/info/warning still auto-dismiss
      // at 4s. Callers can override either way by passing `duration`
      // explicitly (e.g. duration: 8000 for a longer success message,
      // or duration: 0 to force any toast to persist).
      const duration = opts.duration ?? (type === 'error' ? 0 : 4000);
      const newToast: Toast = {
        id,
        message: opts.message,
        type,
        duration,
      };

      setToasts((prev) => {
        const next = [...prev, newToast];
        // Keep only the newest MAX_TOASTS
        return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
      });

      // Auto-dismiss
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast, toasts }), [toast, toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
          <AnimatePresence initial={false}>
            {toasts.map((t) => (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, x: 80 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 80 }}
                transition={{ duration: 0.2 }}
                className={cn(
                  'pointer-events-auto flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg',
                  typeStyles[t.type],
                )}
              >
                <span className="shrink-0">{typeIcons[t.type]}</span>
                <span className="text-sm font-medium flex-1">{t.message}</span>
                <button
                  type="button"
                  onClick={() => dismiss(t.id)}
                  className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export type { Toast, ToastContextValue };
